import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, Video, Headphones, AlertCircle, ExternalLink, ChevronDown, ChevronUp, Download } from 'lucide-react';

const Warcliv = () => {
  const { t } = useLanguage();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const pioneers = [
    {
      name: 'Dr. Thomas Seyfried',
      affiliation: t('Boston College - Biology Professor', 'Universidad de Boston College - Profesor de Biología'),
      contribution: t(
        'Revived Warburg\'s metabolic theory with modern evidence. Author of "Cancer as a Metabolic Disease" (2012). Proposes cancer originates from mitochondrial damage. Developed "Press-Pulse" therapeutic strategy combining metabolic stress with targeted drugs.',
        'Revivió la teoría metabólica de Warburg con evidencia moderna. Autor de "El Cáncer como Enfermedad Metabólica" (2012). Propone que el cáncer se origina por daño mitocondrial. Desarrolló estrategia terapéutica "Press-Pulse" combinando estrés metabólico con fármacos dirigidos.'
      ),
      website: 'https://tomseyfried.com/',
      youtube: 'https://youtube.com/@talkingcancer-u4k',
      paper: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3941741/',
      podcasts: [
        { name: 'Peter Attia Drive #47, #127', url: 'https://peterattiamd.com/' },
        { name: 'Mind & Matter #224', url: 'https://mindandmatter.substack.com/p/cancer-biology-metabolism-mitochondria' },
      ],
    },
    {
      name: 'Dr. Dominic D\'Agostino',
      affiliation: t('University of South Florida - Associate Professor', 'Universidad del Sur de Florida - Profesor Asociado'),
      contribution: t(
        'Translates metabolic science into clinical applications. Research on ketogenic diets, hyperbaric oxygen, and metabolic therapies for cancer. Focuses on practical implementation of metabolic approaches in oncology.',
        'Traduce ciencia metabólica en aplicaciones clínicas. Investiga dietas cetogénicas, oxígeno hiperbárico y terapias metabólicas para cáncer. Enfocado en implementación práctica de enfoques metabólicos en oncología.'
      ),
      website: 'http://www.ketonutrition.org',
      podcasts: [
        { name: 'Tim Ferriss Show #117', url: 'https://tim.blog/2015/11/03/dominic-dagostino/' },
        { name: 'Joe Rogan Experience #994, #1131, #1542', url: 'https://youtube.com/watch?v=u93oh9kC-rU' },
        { name: 'FoundMyFitness', url: 'https://www.foundmyfitness.com/episodes/dominic-dagostino-2' },
      ],
    },
    {
      name: 'Dr. Valter Longo',
      affiliation: t('USC & IFOM Milan - Longevity Institute Director', 'USC e IFOM Milán - Director del Instituto de Longevidad'),
      contribution: t(
        'Discovered "Differential Stress Resistance" - fasting protects normal cells while sensitizing cancer cells to treatment. Developed Fasting-Mimicking Diet (FMD) protocols. Research shows fasting cycles can retard tumor growth and enhance chemotherapy effectiveness.',
        'Descubrió "Resistencia Diferencial al Estrés" - el ayuno protege células normales mientras sensibiliza células cancerosas al tratamiento. Desarrolló protocolos de Dieta Mimética de Ayuno (FMD). Investigación muestra que ciclos de ayuno pueden retardar crecimiento tumoral y mejorar efectividad de quimioterapia.'
      ),
      website: 'https://prolonfmd.com/',
      podcasts: [
        { name: 'FoundMyFitness (Multiple)', url: 'https://www.foundmyfitness.com/' },
      ],
    },
    {
      name: 'Dr. Lewis Cantley',
      affiliation: t('Weill Cornell Medicine - Meyer Cancer Center', 'Medicina Weill Cornell - Centro de Cáncer Meyer'),
      contribution: t(
        'Discovered PI3K pathway - connecting genetic mutations to metabolic reprogramming. Showed how genetics drives metabolism in cancer. Research on glucose-restricted diets and metabolic vulnerabilities. Demonstrates genetics and metabolism are inseparable in cancer.',
        'Descubrió vía PI3K - conectando mutaciones genéticas con reprogramación metabólica. Mostró cómo la genética impulsa el metabolismo en cáncer. Investigación sobre dietas con restricción de glucosa y vulnerabilidades metabólicas. Demuestra que genética y metabolismo son inseparables en cáncer.'
      ),
      podcasts: [
        { name: 'Peter Attia Drive #88', url: 'https://peterattiamd.com/' },
      ],
    },
    {
      name: 'Dr. Jason Fung',
      affiliation: t('Nephrologist & Metabolic Health Educator', 'Nefrólogo y Educador de Salud Metabólica'),
      contribution: t(
        'Author of "The Cancer Code" (2020) - most accessible introduction to metabolic theory for general public. Popularized intermittent fasting. Makes complex metabolic science understandable. Bridge between academic research and patient education.',
        'Autor de "El Código del Cáncer" (2020) - introducción más accesible a teoría metabólica para público general. Popularizó ayuno intermitente. Hace la ciencia metabólica compleja comprensible. Puente entre investigación académica y educación de pacientes.'
      ),
      website: 'https://thefastingmethod.com/',
    },
    {
      name: t('Oncologiametabolica.com (Alfonso Fernández)', 'Oncologiametabolica.com (Alfonso Fernández)'),
      affiliation: t('Leading Spanish Platform on Metabolic Oncology', 'Plataforma Líder Española en Oncología Metabólica'),
      contribution: t(
        'Spanish-language educational resource providing practical metabolic protocols, case analysis, evidence-based content, patient forums, and comprehensive educational materials. Bridge between research and patient community.',
        'Recurso educativo en español que proporciona protocolos metabólicos prácticos, análisis de casos, contenido basado en evidencia, foros de pacientes y materiales educativos comprehensivos. Puente entre investigación y comunidad de pacientes.'
      ),
      website: 'https://oncologiametabolica.com/',
      twitter: 'https://x.com/OncoMetabolica',
    },
    {
      name: 'Dr. Ernesto Prieto Gratacós',
      affiliation: t('Centro de Terapia Metabólica, Buenos Aires', 'Centro de Terapia Metabólica, Buenos Aires'),
      contribution: t(
        'Pioneer in Spanish-speaking world. Author of "Principia Metabolica" and "Terapia Metabólica del Cáncer". Focuses on enzyme inhibition and practical metabolic therapy protocols. Extensive clinical experience in metabolic cancer treatment.',
        'Pionero en el mundo hispanohablante. Autor de "Principia Metabolica" y "Terapia Metabólica del Cáncer". Enfocado en inhibición enzimática y protocolos prácticos de terapia metabólica. Extensa experiencia clínica en tratamiento metabólico del cáncer.'
      ),
      website: 'https://www.ernestoprietogratacos.com/',
      center: 'https://terapiametabolica.com/',
      podcast: 'https://www.fitnessrevolucionario.com/2018/07/25/episodio-145-cancer-enfermedad-metabolica-ernesto-prieto-gratacos/',
    },
  ];

  const faqItems = [
    {
      q: t('Why isn\'t metabolic therapy the standard of care if Warburg discovered this in 1931?', '¿Por qué la terapia metabólica no es el estándar de cuidado si Warburg descubrió esto en 1931?'),
      a: t(
        'Cancer research shifted to genetics (1950s-2000s) after DNA discovery. Warburg\'s theory was set aside for decades. Only since 2000s has technology advanced enough to validate his insights. Currently: metabolic approaches are emerging (ketogenic diets in clinical trials, metabolic drugs in development, major pharma investing). Change takes time - we\'re in the renaissance phase now.',
        'La investigación del cáncer se desplazó hacia la genética (1950s-2000s) después del descubrimiento del ADN. La teoría de Warburg se dejó de lado durante décadas. Solo desde 2000s la tecnología ha avanzado lo suficiente para validar sus conocimientos. Actualmente: enfoques metabólicos están emergiendo (dietas cetogénicas en ensayos clínicos, fármacos metabólicos en desarrollo, grandes farmacéuticas invirtiendo). El cambio lleva tiempo - estamos en la fase de renacimiento ahora.'
      )
    },
    {
      q: t('Can metabolic therapy cure cancer by itself?', '¿Puede la terapia metabólica curar el cáncer por sí sola?'),
      a: t(
        'Metabolic approaches are most effective as part of comprehensive strategy: Diet + fasting can improve outcomes but rarely cure alone. Metabolic drugs show promise but need more research. Best results: metabolic approach + conventional therapy (chemotherapy, immunotherapy, surgery). Example: Fasting before chemo protects normal cells while making cancer more vulnerable. Metasigna\'s approach combines metabolic targeting with cytotoxic payload for dual mechanism.',
        'Los enfoques metabólicos son más efectivos como parte de estrategia comprehensiva: Dieta + ayuno pueden mejorar resultados pero raramente curan solos. Fármacos metabólicos muestran promesa pero necesitan más investigación. Mejores resultados: enfoque metabólico + terapia convencional (quimioterapia, inmunoterapia, cirugía). Ejemplo: Ayuno antes de quimio protege células normales mientras hace el cáncer más vulnerable. El enfoque de Metasigna combina direccionamiento metabólico con carga citotóxica para mecanismo dual.'
      )
    },
    {
      q: t('Is the ketogenic diet safe for cancer patients?', '¿Es segura la dieta cetogénica para pacientes con cáncer?'),
      a: t(
        'Generally safe under medical supervision but NOT for everyone. Contraindications: certain metabolic disorders, pancreatic insufficiency, cachexia (severe weight loss). Benefits documented: reduced glucose availability for tumors, improved insulin sensitivity, reduced inflammation, enhanced chemotherapy effectiveness. CRITICAL: Must work with qualified healthcare team. Not recommended: implementing alone without medical guidance, discontinuing conventional treatment, extreme calorie restriction without supervision.',
        'Generalmente segura bajo supervisión médica pero NO para todos. Contraindicaciones: ciertos trastornos metabólicos, insuficiencia pancreática, caquexia (pérdida severa de peso). Beneficios documentados: disponibilidad reducida de glucosa para tumores, sensibilidad mejorada a insulina, inflamación reducida, efectividad mejorada de quimioterapia. CRÍTICO: Debe trabajar con equipo de salud calificado. No recomendado: implementar solo sin orientación médica, discontinuar tratamiento convencional, restricción calórica extrema sin supervisión.'
      )
    },
    {
      q: t('Does sugar "feed" cancer?', '¿El azúcar "alimenta" el cáncer?'),
      a: t(
        'Nuanced answer: YES - cancer cells preferentially consume glucose (Warburg Effect). FDG-PET scans exploit this. High glucose/insulin environments promote cancer growth. BUT: Eliminating all sugar doesn\'t starve cancer - body makes glucose from protein (gluconeogenesis). Practical approach: Reduce refined sugars and processed carbs. Consider lower-carb diet under guidance. Focus on metabolic health (insulin sensitivity, inflammation). Don\'t obsess over every gram - stress is also harmful. Metabolic therapy is about optimizing entire metabolic environment, not just eliminating sugar.',
        'Respuesta matizada: SÍ - células cancerosas consumen glucosa preferentemente (Efecto Warburg). Escaneos FDG-PET explotan esto. Ambientes de alta glucosa/insulina promueven crecimiento del cáncer. PERO: Eliminar todo el azúcar no mata de hambre al cáncer - el cuerpo hace glucosa de proteína (gluconeogénesis). Enfoque práctico: Reducir azúcares refinados y carbohidratos procesados. Considerar dieta baja en carbohidratos bajo orientación. Enfocarse en salud metabólica (sensibilidad insulina, inflamación). No obsesionarse con cada gramo - el estrés también es dañino. Terapia metabólica se trata de optimizar todo el ambiente metabólico, no solo eliminar azúcar.'
      )
    },
    {
      q: t('How does Metasigna\'s approach relate to metabolic oncology?', '¿Cómo se relaciona el enfoque de Metasigna con la oncología metabólica?'),
      a: t(
        'Direct application of Warburg Effect: Cancer cells overexpress nutrient transporters to fuel rapid growth. Our therapeutic metabolic ligands exploit this - enter via transporters (vectorial function) AND disrupt tumor metabolism (therapeutic function). Dual mechanism: 1) Metabolic attack - ligand itself damages tumor metabolism, 2) Drug delivery - releases cytotoxic payload intratumorally. Addresses Warburg at molecular level - not just dietary intervention but pharmacological exploitation of metabolic vulnerabilities. Next generation beyond diet/fasting - precision metabolic targeting combined with conventional cytotoxic therapy.',
        'Aplicación directa del Efecto Warburg: Células cancerosas sobreexpresan transportadores de nutrientes para impulsar crecimiento rápido. Nuestros ligandos metabólicos terapéuticos explotan esto - entran vía transportadores (función vectorial) Y interrumpen metabolismo tumoral (función terapéutica). Mecanismo dual: 1) Ataque metabólico - ligando mismo daña metabolismo tumoral, 2) Entrega de fármaco - libera carga citotóxica intratumoralmente. Aborda a Warburg a nivel molecular - no solo intervención dietética sino explotación farmacológica de vulnerabilidades metabólicas. Próxima generación más allá de dieta/ayuno - direccionamiento metabólico de precisión combinado con terapia citotóxica convencional.'
      )
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t('WARCLIV - Understanding Cancer as a Metabolic Disease', 'WARCLIV - Entendiendo el Cáncer como Enfermedad Metabólica')}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-amber-100 max-w-4xl">
            {t(
              'Educational Resources on the Metabolic Origins of Cancer and Therapeutic Implications. From Otto Warburg\'s Nobel Prize Discovery to Modern Metabolic Therapies.',
              'Recursos Educativos sobre los Orígenes Metabólicos del Cáncer e Implicaciones Terapéuticas. Desde el Descubrimiento Premio Nobel de Otto Warburg hasta las Terapias Metabólicas Modernas.'
            )}
          </p>
          
          {/* Metasigna/Clivamed/Warcliv Structure */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 mt-8">
            <h3 className="text-2xl font-bold mb-4 text-[#FFD700]">
              {t('Our Three Brands - Integrated Mission', 'Nuestras Tres Marcas - Misión Integrada')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-semibold mb-2">METASIGNA®</h4>
                <p className="text-sm text-amber-100">
                  {t('Pharmaceutical company developing dual-function metabolic platforms for cancer treatment', 'Compañía farmacéutica desarrollando plataformas metabólicas de doble función para tratamiento del cáncer')}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">CLIVAMED®</h4>
                <p className="text-sm text-amber-100">
                  {t('Therapeutic technology platform: cleavable metabolic conjugates with vectorial + therapeutic dual function', 'Plataforma de tecnología terapéutica: conjugados metabólicos clivables con doble función vectorial + terapéutica')}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">WARCLIV®</h4>
                <p className="text-sm text-amber-100">
                  {t('Educational brand honoring Otto Warburg. Cleavable directed therapeutic molecules based on metabolic oncology', 'Marca educativa en homenaje a Otto Warburg. Moléculas terapéuticas dirigidas clivables basadas en oncología metabólica')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 max-w-7xl mx-auto my-8">
        <div className="flex items-start">
          <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              {t('Important Medical Disclaimer', 'Importante Descargo de Responsabilidad Médica')}
            </h3>
            <p className="text-sm text-yellow-700">
              {t(
                'This content is for educational purposes only and does not constitute medical advice. Always consult with qualified healthcare professionals before making any treatment decisions. Do not discontinue prescribed treatments without professional guidance. Metasigna is an investigational pharmaceutical development company - our technologies are in preclinical development and not yet approved for human use.',
                'Este contenido es solo para fines educativos y no constituye consejo médico. Siempre consulte con profesionales de salud calificados antes de tomar decisiones de tratamiento. No discontinúe tratamientos prescritos sin orientación profesional. Metasigna es una compañía farmacéutica de desarrollo investigacional - nuestras tecnologías están en desarrollo preclínico y aún no aprobadas para uso humano.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Otto Warburg Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-amber-700">
            {t('Otto Warburg - The Nobel Laureate Who Changed Everything', 'Otto Warburg - El Premio Nobel que Cambió Todo')}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">
                {t('The Discovery (1920s-1930s)', 'El Descubrimiento (1920s-1930s)')}
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {t(
                  'In 1931, German biochemist Dr. Otto Warburg received the Nobel Prize in Physiology or Medicine for discovering that cancer cells generate energy differently than normal cells.',
                  'En 1931, el bioquímico alemán Dr. Otto Warburg recibió el Premio Nobel en Fisiología o Medicina por descubrir que las células cancerosas generan energía de manera diferente a las células normales.'
                )}
              </p>
              
              <div className="bg-white rounded-lg p-6 border border-amber-200 mb-4">
                <h4 className="font-semibold text-amber-700 mb-3">
                  {t('What Warburg Observed:', 'Lo que Warburg Observó:')}
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>
                      {t(
                        'Normal cells use mitochondria for efficient energy production (OXPHOS)',
                        'Las células normales usan mitocondrias para producción eficiente de energía (OXPHOS)'
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>
                      {t(
                        'Cancer cells prefer less efficient glycolysis even with oxygen present (Warburg Effect)',
                        'Las células cancerosas prefieren glucólisis menos eficiente incluso con oxígeno presente (Efecto Warburg)'
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>
                      {t('This produces less energy but is much faster, creating lactate as byproduct', 'Esto produce menos energía pero es mucho más rápido, creando lactato como subproducto')}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-lg p-6 border border-amber-300">
                <h4 className="font-semibold text-amber-800 mb-2">{t('Warburg\'s Quote:', 'Cita de Warburg:')}</h4>
                <p className="text-gray-700 italic text-sm">
                  "{t(
                    'Cancer, above all other diseases, has countless secondary causes. But, even for cancer, there is only one prime cause... the replacement of the respiration of oxygen in normal body cells by a fermentation of sugar.',
                    'El cáncer, por encima de todas las demás enfermedades, tiene innumerables causas secundarias. Pero, incluso para el cáncer, solo hay una causa primaria... el reemplazo de la respiración de oxígeno en células corporales normales por una fermentación de azúcar.'
                  )}"
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">
                {t('The Theory Evolution', 'La Evolución de la Teoría')}
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-amber-700 mb-2">
                    {t('The Genetic Theory Takes Over (1950s-1980s)', 'La Teoría Genética Toma el Control (1950s-1980s)')}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {t(
                      'Cancer research shifted towards genetics with DNA discoveries. Warburg\'s metabolic theory was set aside for decades as scientists focused on mutations and oncogenes.',
                      'La investigación del cáncer se desplazó hacia la genética con descubrimientos del ADN. La teoría metabólica de Warburg se dejó de lado durante décadas mientras los científicos se enfocaban en mutaciones y oncogenes.'
                    )}
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-amber-700 mb-2">
                    {t('The Renaissance (2000s-Present)', 'El Renacimiento (2000s-Presente)')}
                  </h4>
                  <p className="text-gray-700 text-sm mb-3">
                    {t(
                      'Modern research has vindicated Warburg. We now understand that metabolic dysfunction plays a central role in cancer. New therapeutic strategies target tumor metabolism.',
                      'La investigación moderna ha reivindicado a Warburg. Ahora entendemos que la disfunción metabólica juega un papel central en el cáncer. Nuevas estrategias terapéuticas se dirigen al metabolismo tumoral.'
                    )}
                  </p>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-800 font-semibold mb-2">
                      {t('FDG-PET Scans - Living Proof:', 'Escaneos FDG-PET - Prueba Viviente:')}
                    </p>
                    <p className="text-xs text-green-700">
                      {t(
                        '40+ million FDG-PET scans performed annually worldwide exploit the Warburg Effect to detect cancer through excessive glucose consumption - direct validation of Warburg\'s discovery.',
                        'Más de 40 millones de escaneos FDG-PET realizados anualmente en todo el mundo explotan el Efecto Warburg para detectar cáncer mediante consumo excesivo de glucosa - validación directa del descubrimiento de Warburg.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Matters for Patients */}
          <div className="mt-12 bg-white rounded-lg p-8 border border-gray-200">
            <h3 className="text-2xl font-bold mb-6 text-amber-700">
              {t('Why This Matters for Patients', 'Por Qué Esto Importa para los Pacientes')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">
                  {t('New Therapeutic Strategies:', 'Nuevas Estrategias Terapéuticas:')}
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span>{t('Dietary interventions (ketogenic diet, fasting)', 'Intervenciones dietéticas (dieta cetogénica, ayuno)')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span>{t('Metabolic drugs (repositioned medications)', 'Fármacos metabólicos (medicamentos reposicionados)')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span>
                      {t(
                        'Metasigna\'s dual-function approach (vectorial + therapeutic targeting)',
                        'Enfoque de doble función de Metasigna (direccionamiento vectorial + terapéutico)'
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span>{t('Combination strategies (metabolic + conventional)', 'Estrategias combinadas (metabólicas + convencionales)')}</span>
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 rounded-lg p-6">
                <h4 className="font-semibold text-amber-800 mb-3">
                  {t('Metasigna\'s Connection to Warburg:', 'Conexión de Metasigna con Warburg:')}
                </h4>
                <p className="text-sm text-gray-700">
                  {t(
                    'Our Clivamed® platform directly exploits the Warburg Effect: cancer cells\' excessive glucose consumption makes them vulnerable to our therapeutic metabolic ligands that enter via overexpressed nutrient transporters and disrupt tumor metabolism while delivering cytotoxic payloads. This dual mechanism - metabolic attack + drug delivery - represents the next generation of Warburg-inspired therapies.',
                    'Nuestra plataforma Clivamed® explota directamente el Efecto Warburg: el consumo excesivo de glucosa de las células cancerosas las hace vulnerables a nuestros ligandos metabólicos terapéuticos que entran vía transportadores de nutrientes sobreexpresados e interrumpen el metabolismo tumoral mientras entregan cargas citotóxicas. Este mecanismo dual - ataque metabólico + entrega de fármacos - representa la próxima generación de terapias inspiradas en Warburg.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* El Caballo de Troya Molecular - Multimedia Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-amber-800">
              {t('The Molecular Trojan Horse - Educational Resources', 'El Caballo de Troya Molecular - Recursos Educativos')}
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              {t(
                'Understanding Metasigna\'s dual-function approach through the perfect metaphor: just as the Greeks used a wooden horse to enter Troy, our therapeutic metabolic ligands exploit cancer\'s own hunger to deliver treatment directly inside tumor cells.',
                'Entendiendo el enfoque de doble función de Metasigna mediante la metáfora perfecta: así como los griegos usaron un caballo de madera para entrar a Troya, nuestros ligandos metabólicos terapéuticos explotan el hambre del cáncer para entregar tratamiento directamente dentro de las células tumorales.'
              )}
            </p>
          </div>

          {/* The Metaphor Explained */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-2 border-amber-200">
            <h3 className="text-2xl font-bold mb-6 text-amber-800 flex items-center">
              <span className="text-4xl mr-3">🐴</span>
              {t('Why "Trojan Horse"?', '¿Por Qué "Caballo de Troya"?')}
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-lg mb-3 text-gray-800">
                  {t('The Ancient Greek Strategy:', 'La Estrategia Griega Antigua:')}
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Greeks built a giant wooden horse', 'Griegos construyeron un caballo de madera gigante')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Trojans brought it inside their walls (thought it was a gift)', 'Troyanos lo llevaron dentro de sus murallas (pensaban que era un regalo)')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Greek soldiers hidden inside emerged at night', 'Soldados griegos escondidos dentro emergieron por la noche')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Troy defeated from within', 'Troya derrotada desde dentro')}</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3 text-gray-800">
                  {t('Metasigna\'s Molecular Strategy:', 'Estrategia Molecular de Metasigna:')}
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Build therapeutic ligand disguised as nutrient', 'Construir ligando terapéutico disfrazado como nutriente')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Cancer cells bring it inside (think it\'s food)', 'Células cancerosas lo llevan dentro (piensan que es alimento)')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Drug payload hidden inside releases in tumor', 'Carga útil de fármaco escondida dentro se libera en tumor')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">•</span>
                    <span>{t('Cancer attacked from within with precision', 'Cáncer atacado desde dentro con precisión')}</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Drug Delivery Illustration */}
            <div className="mt-8 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/ku4wbfyj_ZgxnnoLGiEpe8SeGLsOrq.png"
                alt={t('Drug delivery mechanism - capsule attacking cancer cell', 'Mecanismo de entrega de fármaco - cápsula atacando célula cancerosa')}
                className="rounded-xl shadow-lg max-w-md w-full h-auto"
              />
            </div>

            <div className="mt-6 bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-gray-800 italic">
                {t(
                  'The key insight: Cancer\'s own metabolic vulnerabilities (excessive nutrient consumption) become its downfall. We don\'t fight cancer head-on - we use its hunger against itself.',
                  'El conocimiento clave: Las propias vulnerabilidades metabólicas del cáncer (consumo excesivo de nutrientes) se convierten en su perdición. No luchamos contra el cáncer de frente - usamos su hambre en su contra.'
                )}
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-amber-800 flex items-center">
              <Video size={28} className="mr-3" />
              {t('Featured Video', 'Video Destacado')}
            </h3>
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h4 className="text-xl font-semibold mb-4 text-gray-800">
                {t('The Cancer Trojan Horse', 'El Caballo de Troya del Cáncer')}
              </h4>
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
                <video 
                  controls 
                  className="w-full h-full"
                  poster="/api/placeholder/800/450"
                >
                  <source src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/ud5sui3h_El_Caballo_de_Troya_del_Ca%CC%81ncer.mp4" type="video/mp4" />
                  {t('Your browser does not support the video tag.', 'Su navegador no soporta la etiqueta de video.')}
                </video>
              </div>
              <p className="text-sm text-gray-600">
                {t(
                  'Visual explanation of how Clivamed® platform works using the Trojan Horse metaphor. Perfect introduction for patients, families, and general public.',
                  'Explicación visual de cómo funciona la plataforma Clivamed® usando la metáfora del Caballo de Troya. Introducción perfecta para pacientes, familias y público general.'
                )}
              </p>
            </div>
          </div>

          {/* Podcasts/Audio Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-amber-800 flex items-center">
              <Headphones size={28} className="mr-3" />
              {t('Educational Podcasts', 'Podcasts Educativos')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">
                  {t('Episode 1: The Molecular Trojan Horse', 'Episodio 1: El Caballo de Troya Molecular')}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t('Spanish patent attacking cancer metabolism from within', 'Patente española atacando metabolismo del cáncer desde dentro')}
                </p>
                <audio controls className="w-full mb-2">
                  <source src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/cygdz75h_El_Caballo_de_Troya_Molecular__La_Patente_Espan%CC%83ola_que_Ataca_el%20%282%29.m4a" type="audio/mp4" />
                  {t('Your browser does not support the audio element.', 'Su navegador no soporta el elemento de audio.')}
                </audio>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">
                  {t('Episode 2: Deciphering the Strategy', 'Episodio 2: Descifrando la Estrategia')}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t('Spanish anti-cancer patent strategy explained', 'Estrategia de patente anticáncer española explicada')}
                </p>
                <audio controls className="w-full mb-2">
                  <source src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/jfex6esh_La_Patente_Antica%CC%81ncer__Descifrando_la_Estrategia_Espan%CC%83ola_de__P%20%281%29.m4a" type="audio/mp4" />
                  {t('Your browser does not support the audio element.', 'Su navegador no soporta el elemento de audio.')}
                </audio>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">
                  {t('Episode 3: Metabolic Trojan Horses', 'Episodio 3: Caballos de Troya Metabólicos')}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t('Spanish patent repurposing drugs with metabolic targeting', 'Patente española reposicionando fármacos con direccionamiento metabólico')}
                </p>
                <audio controls className="w-full mb-2">
                  <source src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/irejnhau_Caballos_de_Troya_Metabo%CC%81licos__La_Patente_Espan%CC%83ola_que_Reutiliz%20%281%29.m4a" type="audio/mp4" />
                  {t('Your browser does not support the audio element.', 'Su navegador no soporta el elemento de audio.')}
                </audio>
              </div>
            </div>
          </div>

          {/* Downloadable Resources */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-amber-800 flex items-center">
              <BookOpen size={28} className="mr-3" />
              {t('Downloadable Resources', 'Recursos Descargables')}
            </h3>
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">
                    {t('The Achilles\' Heel of Cancer', 'El Talón de Aquiles del Cáncer')}
                  </h4>
                  <p className="text-gray-600 mb-4">
                    {t(
                      'Comprehensive PDF guide explaining cancer\'s metabolic vulnerabilities and how they can be exploited therapeutically. Spanish language.',
                      'Guía completa en PDF explicando las vulnerabilidades metabólicas del cáncer y cómo pueden ser explotadas terapéuticamente. En español.'
                    )}
                  </p>
                </div>
                <a
                  href="https://customer-assets.emergentagent.com/job_aa7b504b-19c8-4236-b84c-defa2e17666a/artifacts/wdwshjff_Talon_Aquiles_WEB_v1.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-6 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center"
                >
                  <Download size={20} className="mr-2" />
                  {t('Download PDF', 'Descargar PDF')}
                </a>
              </div>
            </div>
          </div>

          {/* Key Takeaways Box */}
          <div className="mt-12 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              {t('Key Takeaways - Why This Matters', 'Puntos Clave - Por Qué Esto Importa')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="font-semibold mb-2">
                  {t('For Patients & Families:', 'Para Pacientes y Familias:')}
                </p>
                <ul className="space-y-1 text-sm opacity-90">
                  <li>• {t('More selective = less toxicity', 'Más selectivo = menos toxicidad')}</li>
                  <li>• {t('Exploits cancer\'s weakness', 'Explota debilidad del cáncer')}</li>
                  <li>• {t('Works with existing treatments', 'Funciona con tratamientos existentes')}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">
                  {t('For Healthcare Professionals:', 'Para Profesionales de Salud:')}
                </p>
                <ul className="space-y-1 text-sm opacity-90">
                  <li>• {t('Platform technology (multiple drugs)', 'Tecnología de plataforma (múltiples fármacos)')}</li>
                  <li>• {t('Accelerated pathways (repositioning)', 'Vías aceleradas (reposicionamiento)')}</li>
                  <li>• {t('Addresses resistance mechanisms', 'Aborda mecanismos de resistencia')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific Foundation Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1F4E78]">
              {t('Scientific Foundation of Our Patent', 'Fundamento Científico de Nuestra Patente')}
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              {t(
                'Metasigna\'s Clivamed® platform is built on decades of peer-reviewed scientific research. Our patent (filed Q1 2025) synthesizes insights from 67+ published studies across multiple disciplines.',
                'La plataforma Clivamed® de Metasigna está construida sobre décadas de investigación científica revisada por pares. Nuestra patente (presentada Q1 2025) sintetiza conocimientos de más de 67 estudios publicados en múltiples disciplinas.'
              )}
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-blue-50 rounded-lg p-6 text-center border border-blue-200">
              <div className="text-4xl font-bold text-[#1F4E78] mb-2">67+</div>
              <div className="text-sm text-gray-700">{t('Scientific References', 'Referencias Científicas')}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 text-center border border-blue-200">
              <div className="text-4xl font-bold text-[#1F4E78] mb-2">35+</div>
              <div className="text-sm text-gray-700">{t('Free Full Text Available', 'Texto Completo Gratis')}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 text-center border border-blue-200">
              <div className="text-4xl font-bold text-[#1F4E78] mb-2">6</div>
              <div className="text-sm text-gray-700">{t('Related Patents', 'Patentes Relacionadas')}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 text-center border border-blue-200">
              <div className="text-4xl font-bold text-[#1F4E78] mb-2">Q1 2025</div>
              <div className="text-sm text-gray-700">{t('Patent Filed (OEPM Spain)', 'Patente Presentada (OEPM España)')}</div>
            </div>
          </div>

          {/* Research Categories */}
          <div className="space-y-6">
            {/* Category 1 - Foundational */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#1F4E78] flex items-center">
                <BookOpen size={24} className="mr-3" />
                {t('1. Foundational Research (10 studies)', '1. Investigación Fundacional (10 estudios)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('Otto Warburg\'s metabolic theory, Thomas Seyfried\'s modern revival, FDG-PET validation, mitochondrial dysfunction', 'Teoría metabólica de Otto Warburg, renacimiento moderno de Thomas Seyfried, validación FDG-PET, disfunción mitocondrial')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/24343361/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Seyfried et al., 2014 - Carcinogenesis (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/12192558/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Gambhir, 2002 - PET imaging
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/18678913/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Chen et al., 2008 - Ascorbate prooxidant (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/25075217/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Pantziarka et al., 2014 - Mebendazole ReDO (PMC Free)
                </a>
              </div>
            </div>

            {/* Category 2 - Repurposed Drugs */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#1F4E78] flex items-center">
                <BookOpen size={24} className="mr-3" />
                {t('2. Repurposed Drugs (11 studies)', '2. Fármacos Reposicionados (11 estudios)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('Ivermectin, Mebendazole, Fenbendazole, Niclosamide, Metformin, Doxycycline, Tretinoin - FDA-approved drugs with anti-cancer properties', 'Ivermectina, Mebendazol, Fenbendazol, Niclosamida, Metformina, Doxiciclina, Tretinoína - Fármacos aprobados por FDA con propiedades anticancerígenas')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/25124677/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Melotti et al., 2014 - Ivermectin WNT (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/21764822/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Bai et al., 2011 - Mebendazole glioblastoma (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/36039737/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Park et al., 2022 - Fenbendazole + 5-FU (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/36381314/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Jiang et al., 2022 - Ivermectin metastasis (PMC Free)
                </a>
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">{t('Note on Ivermectin:', 'Nota sobre Ivermectina:')}</span> {t('William C. Campbell (Nobel Prize 2015) discovered ivermectin for parasitic diseases. Current research by others (Dr. Makis, et al.) explores anti-cancer applications. Metasigna\'s Clivamed® conjugation enhances ivermectin\'s anti-tumor capacity through metabolic targeting and specific tumor cell delivery.', 'William C. Campbell (Premio Nobel 2015) descubrió ivermectina para enfermedades parasitarias. Investigación actual por otros (Dr. Makis, et al.) explora aplicaciones anticáncer. La conjugación Clivamed® de Metasigna mejora la capacidad antitumoral de ivermectina mediante direccionamiento metabólico y entrega específica a células tumorales.')}
                </p>
              </div>
            </div>

            {/* Category 3 - Metabolic Ligands */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#1F4E78] flex items-center">
                <BookOpen size={24} className="mr-3" />
                {t('3. Metabolic Ligands & Transporters (6 studies)', '3. Ligandos Metabólicos y Transportadores (6 estudios)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('Nutrient transporter targeting, DHA transport, 3-bromopyruvate, mitochondrial targeting, antifolates, lipoic acid', 'Direccionamiento de transportadores de nutrientes, transporte DHA, 3-bromopiruvato, direccionamiento mitocondrial, antifolatos, ácido lipoico')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/18992769/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Ganapathy et al., 2009 - Nutrient transporters (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/9235947/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Rumsey et al., 1997 - GLUT1/3 DHA transport
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/15465013/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Ko et al., 2004 - 3-bromopyruvate ATP
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/24970811/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Pelicano et al., 2014 - Mitochondria Akt (PMC Free)
                </a>
              </div>
            </div>

            {/* Category 4 - Formulation & Delivery */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#1F4E78] flex items-center">
                <BookOpen size={24} className="mr-3" />
                {t('4. Drug Formulation & Delivery (8 studies)', '4. Formulación y Entrega de Fármacos (8 estudios)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('Mannose targeting, PLGA nanoparticles, liposomes, EPR effect, DCA, methylene blue, naltrexone', 'Direccionamiento de manosa, nanopartículas PLGA, liposomas, efecto EPR, DCA, azul de metileno, naltrexona')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/30283720/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Gonzalez et al., 2018 - Mannose (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/25678787/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Bozzuto & Molinari, 2015 - Liposomes (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/10699287/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Maeda et al., 2000 - EPR effect
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/20484731/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Michelakis et al., 2010 - DCA glioblastoma
                </a>
              </div>
            </div>

            {/* Category 5 - Glutathione & ROS */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#1F4E78] flex items-center">
                <BookOpen size={14} className="mr-3" />
                {t('5. Glutathione & Oxidative Stress (3 studies)', '5. Glutatión y Estrés Oxidativo (3 estudios)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('GSH synthesis, chemoresistance mechanisms, ascorbate-mediated H2O2 generation', 'Síntesis GSH, mecanismos de quimioresistencia, generación H2O2 mediada por ascorbato')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a href="https://pubmed.ncbi.nlm.nih.gov/29915025/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Bansal & Simon, 2018 - GSH resistance (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/16157892/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Chen et al., 2005 - Ascorbate H2O2 (PMC Free)
                </a>
                <a href="https://pubmed.ncbi.nlm.nih.gov/23833758/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={14} className="mr-1" />
                  Traverso et al., 2013 - GSH chemoresistance (PMC Free)
                </a>
              </div>
            </div>

            {/* Compact Categories 6-8 */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold mb-3 text-[#1F4E78]">
                  {t('6. Immunoconjugates (4)', '6. Inmunoconjugados (4)')}
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  {t('ADC prospects, PSMA immunotherapies, radiolabeled conjugates', 'Perspectivas ADC, inmunoterapias PSMA, conjugados radiomarcados')}
                </p>
                <a href="https://pubmed.ncbi.nlm.nih.gov/28898207/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={12} className="mr-1" />
                  Wu & Senter, 2017 - ADCs
                </a>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold mb-3 text-[#1F4E78]">
                  {t('7. Leukemias & Hematology (8)', '7. Leucemias y Hematología (8)')}
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  {t('Venetoclax, Imatinib, Azacitidine, Arsenic, HDAC inhibitors, nucleoside analogues', 'Venetoclax, Imatinib, Azacitidina, Arsénico, inhibidores HDAC, análogos de nucleósidos')}
                </p>
                <a href="https://pubmed.ncbi.nlm.nih.gov/27520294/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                  <ExternalLink size={12} className="mr-1" />
                  Konopleva et al., 2016 - Venetoclax (PMC Free)
                </a>
              </div>
            </div>

            {/* Download Full Bibliography */}
            <div className="bg-gradient-to-r from-[#1F4E78] to-[#2E5090] rounded-xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">
                {t('Access Complete Bibliography', 'Acceder a Bibliografía Completa')}
              </h3>
              <p className="mb-6 max-w-2xl mx-auto">
                {t(
                  'Access the complete list of 67 scientific references with direct PubMed/DOI links. Organized by category with PMC free full-text indicators.',
                  'Acceda a la lista completa de 67 referencias científicas con enlaces directos a PubMed/DOI. Organizada por categorías con indicadores de texto completo gratuito en PMC.'
                )}
              </p>
              <a
                href="/documents/Bibliografia_Cientifica_Metasigna.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-[#FFD700] text-[#1F4E78] rounded-lg font-bold hover:bg-yellow-400 transition-colors"
              >
                <Download size={24} className="mr-3" />
                {t('View Bibliography (Interactive)', 'Ver Bibliografía (Interactiva)')}
              </a>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="mt-12 bg-blue-50 border-2 border-blue-200 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-[#1F4E78]">
              {t('Why Scientific Foundation Matters', 'Por Qué Importa el Fundamento Científico')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="font-semibold mb-2 text-gray-800">{t('For Patients:', 'Para Pacientes:')}</p>
                <p className="text-sm text-gray-700">
                  {t('Not experimental guesswork - built on decades of peer-reviewed research from top institutions', 'No es especulación experimental - construido sobre décadas de investigación revisada por pares de instituciones líderes')}
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2 text-gray-800">{t('For Investors:', 'Para Inversores:')}</p>
                <p className="text-sm text-gray-700">
                  {t('De-risked approach - multiple validating precedents, established mechanisms, reproducible results', 'Enfoque de riesgo reducido - múltiples precedentes validadores, mecanismos establecidos, resultados reproducibles')}
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2 text-gray-800">{t('For Scientists:', 'Para Científicos:')}</p>
                <p className="text-sm text-gray-700">
                  {t('Rigorous synthesis - patent integrates insights across oncology, metabolism, formulation, delivery', 'Síntesis rigurosa - patente integra conocimientos en oncología, metabolismo, formulación, entrega')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Pioneers Section - Continuing... */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-amber-700">
            {t('The Scientists Advancing Metabolic Cancer Research Today', 'Los Científicos Avanzando la Investigación Metabólica del Cáncer Hoy')}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            {t(
              'A new generation of researchers, building on Warburg\'s foundation, is exploiting cancer\'s metabolic vulnerabilities to develop novel therapeutic approaches.',
              'Una nueva generación de investigadores, construyendo sobre la base de Warburg, está explotando las vulnerabilidades metabólicas del cáncer para desarrollar enfoques terapéuticos novedosos.'
            )}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {pioneers.map((pioneer, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold mb-2 text-amber-700">{pioneer.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{pioneer.affiliation}</p>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">{pioneer.contribution}</p>
                
                <div className="space-y-2">
                  {pioneer.website && (
                    <a
                      href={pioneer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-amber-600 hover:text-amber-800"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      {t('Official Website', 'Sitio Web Oficial')}
                    </a>
                  )}
                  {pioneer.youtube && (
                    <a
                      href={pioneer.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-amber-600 hover:text-amber-800"
                    >
                      <Video size={16} className="mr-2" />
                      YouTube Channel
                    </a>
                  )}
                  {pioneer.paper && (
                    <a
                      href={pioneer.paper}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-amber-600 hover:text-amber-800"
                    >
                      <BookOpen size={16} className="mr-2" />
                      {t('Key Paper', 'Artículo Clave')}
                    </a>
                  )}
                  {pioneer.podcasts && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-600 mb-1 flex items-center">
                        <Headphones size={14} className="mr-1" />
                        {t('Featured Podcasts:', 'Podcasts Destacados:')}
                      </p>
                      <ul className="space-y-1 ml-5">
                        {pioneer.podcasts.map((podcast, i) => (
                          <li key={i}>
                            <a
                              href={podcast.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-amber-600 hover:text-amber-800"
                            >
                              {podcast.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pioneer.center && (
                    <a
                      href={pioneer.center}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-amber-600 hover:text-amber-800"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      {t('Treatment Center', 'Centro de Tratamiento')}
                    </a>
                  )}
                  {pioneer.twitter && (
                    <a
                      href={pioneer.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-amber-600 hover:text-amber-800"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Twitter/X
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metabolic Therapies Landscape */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-amber-700">
            {t('Metabolic Cancer Therapies - Current Landscape', 'Terapias Metabólicas del Cáncer - Panorama Actual')}
          </h2>
          
          <div className="space-y-8">
            {/* Tier 1 - Dietary Interventions */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold mb-4 text-green-700">
                {t('Tier 1: Dietary & Lifestyle Interventions', 'Nivel 1: Intervenciones Dietéticas y de Estilo de Vida')}
              </h3>
              <p className="text-gray-700 mb-4">{t('Accessible to most patients, safe under guidance', 'Accesible para mayoría de pacientes, seguro bajo orientación')}</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('Ketogenic Diet', 'Dieta Cetogénica')}</h4>
                  <p className="text-sm text-gray-600">{t('High fat, very low carb. Clinical trials ongoing. Best for brain tumors, glioblastoma.', 'Alta grasa, muy baja en carbohidratos. Ensayos clínicos en curso. Mejor para tumores cerebrales, glioblastoma.')}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('Fasting / Fasting-Mimicking Diet', 'Ayuno / Dieta Mimética de Ayuno')}</h4>
                  <p className="text-sm text-gray-600">{t('Cycles of calorie restriction. Protects normal cells, sensitizes cancer cells. FMD protocols available.', 'Ciclos de restricción calórica. Protege células normales, sensibiliza células cancerosas. Protocolos FMD disponibles.')}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('Time-Restricted Eating', 'Alimentación con Restricción de Tiempo')}</h4>
                  <p className="text-sm text-gray-600">{t('16:8 or similar. Improves insulin sensitivity, reduces inflammation. Easier to implement long-term.', '16:8 o similar. Mejora sensibilidad insulina, reduce inflamación. Más fácil de implementar largo plazo.')}</p>
                </div>
              </div>
            </div>

            {/* Tier 2 - Repurposed Drugs */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold mb-4 text-blue-700">
                {t('Tier 2: Repurposed Metabolic Drugs', 'Nivel 2: Fármacos Metabólicos Reposicionados')}
              </h3>
              <p className="text-gray-700 mb-4">{t('FDA-approved for other uses, investigated for cancer (off-label)', 'Aprobados por FDA para otros usos, investigados para cáncer (off-label)')}</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">Metformin:</p>
                  <p className="text-gray-600">{t('Diabetes drug, targets mitochondria. Epidemiological studies show reduced cancer risk.', 'Fármaco para diabetes, dirige mitocondrias. Estudios epidemiológicos muestran riesgo reducido de cáncer.')}</p>
                </div>
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">Dichloroacetate (DCA):</p>
                  <p className="text-gray-600">{t('Shifts metabolism from glycolysis to OXPHOS. Some clinical use, needs more research.', 'Cambia metabolismo de glucólisis a OXPHOS. Algo de uso clínico, necesita más investigación.')}</p>
                </div>
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">Ivermectin:</p>
                  <p className="text-gray-600">{t('Anti-parasitic drug now investigated for anti-cancer properties. Metasigna enhancing via Clivamed conjugation.', 'Fármaco antiparasitario ahora investigado por propiedades anticancerígenas. Metasigna mejorando vía conjugación Clivamed.')}</p>
                </div>
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">{t('Others', 'Otros')}:</p>
                  <p className="text-gray-600">{t('Aspirin, statins, mebendazole - various metabolic effects under investigation.', 'Aspirina, estatinas, mebendazol - varios efectos metabólicos bajo investigación.')}</p>
                </div>
              </div>
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-4">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">{t('Important:', 'Importante:')}</span> {t('Off-label use requires medical supervision. Evidence quality varies. Not recommended as monotherapy.', 'Uso off-label requiere supervisión médica. Calidad de evidencia varía. No recomendado como monoterapia.')}
                </p>
              </div>
            </div>

            {/* Tier 3 - Clinical Trial Drugs */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold mb-4 text-purple-700">
                {t('Tier 3: Novel Metabolic Drugs (Clinical Trials)', 'Nivel 3: Nuevos Fármacos Metabólicos (Ensayos Clínicos)')}</h3>
              <p className="text-gray-700 mb-4">{t('Purpose-designed drugs targeting cancer metabolism, in human trials', 'Fármacos diseñados específicamente dirigiendo metabolismo del cáncer, en ensayos humanos')}</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2 mt-1">•</span>
                  <span><span className="font-semibold">IDH inhibitors</span> - {t('FDA approved for certain leukemias/gliomas (Ivosidenib, Enasidenib)', 'Aprobados por FDA para ciertas leucemias/gliomas (Ivosidenib, Enasidenib)')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2 mt-1">•</span>
                  <span><span className="font-semibold">LDHA inhibitors</span> - {t('Multiple companies developing, targets lactate production', 'Múltiples compañías desarrollando, dirige producción de lactato')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2 mt-1">•</span>
                  <span><span className="font-semibold">Glutaminase inhibitors</span> - {t('Blocks glutamine addiction in certain cancers', 'Bloquea adicción a glutamina en ciertos cánceres')}</span>
                </li>
              </ul>
            </div>

            {/* Tier 4 - Metasigna */}
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-8 border-2 border-amber-300">
              <h3 className="text-2xl font-bold mb-4 text-amber-800">
                {t('Tier 4: Next Generation - Dual-Function Metabolic Platforms', 'Nivel 4: Próxima Generación - Plataformas Metabólicas de Doble Función')}
              </h3>
              <p className="text-gray-800 mb-4 font-semibold">
                {t('Metasigna\'s Clivamed® - Vectorial + Therapeutic Targeting', 'Clivamed® de Metasigna - Direccionamiento Vectorial + Terapéutico')}
              </p>
              <div className="bg-white rounded-lg p-6 mb-4">
                <p className="text-gray-700 mb-3">
                  {t(
                    'Unlike all above approaches, our therapeutic metabolic ligands have DUAL FUNCTIONALITY:',
                    'A diferencia de todos los enfoques anteriores, nuestros ligandos metabólicos terapéuticos tienen DOBLE FUNCIONALIDAD:'
                  )}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span><span className="font-semibold">{t('Vectorial', 'Vectorial')}:</span> {t('Enter tumor cells via overexpressed nutrient transporters (5-10x selectivity)', 'Entran células tumorales vía transportadores de nutrientes sobreexpresados (selectividad 5-10x)')}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span><span className="font-semibold">{t('Therapeutic', 'Terapéutica')}:</span> {t('Ligand itself disrupts tumor metabolism immediately upon entry', 'Ligando mismo interrumpe metabolismo tumoral inmediatamente al entrar')}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-600 mr-2 mt-1">▸</span>
                    <span><span className="font-semibold">{t('Plus Payload', 'Más Carga Útil')}:</span> {t('Releases conjugated drug intratumorally for triple mechanism', 'Libera fármaco conjugado intratumoralmente para mecanismo triple')}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                {t(
                  'Preclinical development. Represents pharmaceutical-grade exploitation of Warburg Effect beyond dietary/lifestyle approaches.',
                  'Desarrollo preclínico. Representa explotación de grado farmacéutico del Efecto Warburg más allá de enfoques dietéticos/estilo de vida.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-amber-700">
            {t('Frequently Asked Questions', 'Preguntas Frecuentes')}
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg text-amber-800 pr-4">{item.q}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="flex-shrink-0 text-amber-700" size={24} />
                  ) : (
                    <ChevronDown className="flex-shrink-0 text-amber-700" size={24} />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-amber-700">
            {t('Interested in Learning More?', '¿Interesado en Aprender Más?')}
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            {t(
              'Warcliv is an educational initiative to make metabolic oncology accessible. We continually update this resource with new research, videos, and educational content.',
              'Warcliv es una iniciativa educativa para hacer la oncología metabólica accesible. Actualizamos continuamente este recurso con nueva investigación, videos y contenido educativo.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:warcliv@metasigna.company"
              className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
            >
              {t('Contact Us', 'Contáctenos')}
            </a>
            <a
              href="/"
              className="px-6 py-3 bg-white text-amber-700 border-2 border-amber-600 rounded-lg font-semibold hover:bg-amber-50 transition-colors"
            >
              {t('Back to Metasigna', 'Volver a Metasigna')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Warcliv;
